import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { COURT_KEYPOINT_LABELS, COURT_KEYPOINT_TYPES, VIDEO_WIDTH, VIDEO_HEIGHT } from '../../utils/constants';
import { convertToNativeCoordinates, convertVideoCoordinates } from '../../utils/annotationUtils';

const CourtOverlayContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: ${props => props.displayWidth || VIDEO_WIDTH}px;
  height: ${props => props.displayHeight || VIDEO_HEIGHT}px;
  z-index: 5;
  pointer-events: auto;
  cursor: crosshair;
  background: transparent;
`;

const CourtBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${theme.colors.courtOverlay};
  pointer-events: none;
`;

const CourtLine = styled.div`
  position: absolute;
  background: ${props => props.color};
  pointer-events: none;
  height: 3px;
`;

const Keypoint = styled.div`
  position: absolute;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${props => props.color};
  border: 2px solid ${theme.colors.text};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${theme.typography.fontWeights.bold};
  font-size: ${theme.typography.fontSizes.sm};
  color: ${theme.colors.text};
  cursor: grab;
  user-select: none;
  z-index: 6;
  transform: translate(-12px, -12px);
  
  &:active {
    cursor: grabbing;
  }
  
  &:hover {
    box-shadow: 0 0 8px ${props => props.color};
  }
  
  ${props => props.outOfFrame && `
    background-color: ${theme.colors.error || '#ff5555'} !important;
    border-color: ${theme.colors.error || '#ff5555'} !important;
    color: white !important;
    
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 0;
      right: 0;
      height: 2px;
      background-color: white;
      transform: translateY(-50%) rotate(-45deg);
    }
  `}
`;

const CourtOverlay = ({ 
  keypoints, 
  onKeypointsChange, 
  onSave, 
  onPredict,
  videoRef, 
  zoom, 
  pan,
  displayDimensions = { width: VIDEO_WIDTH, height: VIDEO_HEIGHT }
}) => {
  const [draggedKeypoint, setDraggedKeypoint] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const overlayRef = useRef(null);

  // Handle both single court (legacy) and multiple courts (new)
  // No defaults - if no keypoints, return early
  if (!keypoints) {
    return null;
  }
  
  const allCourts = Array.isArray(keypoints) ? keypoints : [{ keypoints, reprojected: false }];
  
  // Primary court (first non-reprojected court or first court)
  const primaryCourt = allCourts.find(court => !court.reprojected) || allCourts[0];
  const currentKeypoints = primaryCourt ? primaryCourt.keypoints : null;
  
  // If no valid keypoints, don't render anything
  if (!currentKeypoints) {
    return null;
  }

  // Get color for keypoint type
  const getKeypointColor = (label, isReprojected = false, isPredicted = false) => {
    // Use different colors for different types
    if (isPredicted) {
      return theme.colors.courtLineService || '#ff6b6b'; // Red for predicted keypoints
    }
    if (isReprojected) {
      return theme.colors.courtLineService || '#ffb86c'; // Orange for reprojected keypoints
    }
    // Use green for primary keypoints (red will be applied via CSS for out-of-frame)
    return theme.colors.courtLineBaseline;
  };

  // Get color for court line type
  const getLineColor = (lineType, isReprojected = false, isPredicted = false) => {
    // Use different colors for different types
    if (isPredicted) {
      return theme.colors.courtLineService || '#ff6b6b'; // Red for predicted lines
    }
    if (isReprojected) {
      return theme.colors.courtLineService || '#ffb86c'; // Orange for reprojected lines
    }
    // Use green for primary lines
    return theme.colors.courtLineBaseline;
  };

  // Handle mouse down on keypoint
  const handleMouseDown = (e, label) => {
    e.preventDefault();
    e.stopPropagation();
    
    const rect = overlayRef.current.getBoundingClientRect();
    const keypoint = currentKeypoints[label];
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Convert mouse position to video coordinates using same formula as annotation click
    const videoX = (mouseX + pan.x) / zoom;
    const videoY = (mouseY + pan.y) / zoom;
    
    setDraggedKeypoint(label);
    setDragOffset({
      x: videoX - keypoint.x,
      y: videoY - keypoint.y
    });
  };

  // Handle right click on keypoint to toggle out-of-frame state
  const handleContextMenu = (e, label) => {
    e.preventDefault();
    e.stopPropagation();
    
    const keypoint = currentKeypoints[label];
    const newKeypoints = {
      ...currentKeypoints,
      [label]: {
        ...keypoint,
        outOfFrame: !keypoint.outOfFrame
      }
    };
    
    onKeypointsChange(newKeypoints);
  };

  // Handle mouse move
  const handleMouseMove = (e) => {
    if (!draggedKeypoint) return;
    
    const rect = overlayRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Convert mouse position to video coordinates using same formula as annotation click
    const videoX = (mouseX + pan.x) / zoom;
    const videoY = (mouseY + pan.y) / zoom;
    
    // Calculate new position in video coordinates
    const newX = videoX - dragOffset.x;
    const newY = videoY - dragOffset.y;
    
    // Get actual video dimensions for proper clamping
    const actualVideoWidth = (videoRef?.current?.width) || VIDEO_WIDTH;
    const actualVideoHeight = (videoRef?.current?.height) || VIDEO_HEIGHT;
    
    // Clamp to actual video bounds
    const clampedX = Math.max(0, Math.min(newX, actualVideoWidth));
    const clampedY = Math.max(0, Math.min(newY, actualVideoHeight));
    
    const newKeypoints = {
      ...currentKeypoints,
      [draggedKeypoint]: { 
        x: clampedX, 
        y: clampedY,
        outOfFrame: currentKeypoints[draggedKeypoint].outOfFrame || false
      }
    };
    
    onKeypointsChange(newKeypoints);
  };

  // Handle mouse up
  const handleMouseUp = () => {
    setDraggedKeypoint(null);
    setDragOffset({ x: 0, y: 0 });
  };

  // Add mouse event listeners
  useEffect(() => {
    if (draggedKeypoint) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [draggedKeypoint, dragOffset, currentKeypoints]);

  // Helper to apply zoom/pan to a point
  const applyZoomPan = (pt) => ({
    x: pt.x * zoom - pan.x,
    y: pt.y * zoom - pan.y
  });

  // Render court lines for a specific court
  const renderCourtLinesForCourt = (courtKeypoints, isReprojected = false, isPredicted = false, zIndex = 1) => {
    if (!courtKeypoints) return null;
    
    // Get video dimensions for coordinate conversion
    const nativeWidth = (videoRef?.current?.width) || VIDEO_WIDTH;
    const nativeHeight = (videoRef?.current?.height) || VIDEO_HEIGHT;
    
    // Complete connected path: A, B, C, D, E, R, P, O, N, M, L, Q, A
    const mainPath = [
      { from: 'A', to: 'B', type: 'baseline' },
      { from: 'B', to: 'C', type: 'baseline' },
      { from: 'C', to: 'D', type: 'baseline' },
      { from: 'D', to: 'E', type: 'baseline' },
      { from: 'E', to: 'R', type: 'baseline' },
      { from: 'R', to: 'P', type: 'baseline' },
      { from: 'P', to: 'O', type: 'baseline' },
      { from: 'O', to: 'N', type: 'baseline' },
      { from: 'N', to: 'M', type: 'baseline' },
      { from: 'M', to: 'L', type: 'baseline' },
      { from: 'L', to: 'Q', type: 'baseline' },
      { from: 'Q', to: 'A', type: 'baseline' }
    ];

    // Service box lines
    const serviceLines = [
      { from: 'F', to: 'G', type: 'baseline' },
      { from: 'G', to: 'H', type: 'baseline' },
      { from: 'I', to: 'J', type: 'baseline' },
      { from: 'J', to: 'K', type: 'baseline' },
      { from: 'J', to: 'G', type: 'baseline' }
    ];

    // Singles sidelines
    const singlesLines = [
      { from: 'M', to: 'I', type: 'baseline' },
      { from: 'I', to: 'F', type: 'baseline' },
      { from: 'F', to: 'B', type: 'baseline' },
      { from: 'O', to: 'K', type: 'baseline' },
      { from: 'K', to: 'H', type: 'baseline' },
      { from: 'H', to: 'D', type: 'baseline' }
    ];

    // Center line
    const centerLine = [
      { from: 'Q', to: 'R', type: 'baseline' }
    ];

    const allLines = [
      ...mainPath,
      ...serviceLines,
      ...singlesLines,
      ...centerLine
    ];

    return allLines.map((line, index) => {
      const fromRaw = courtKeypoints[line.from];
      const toRaw = courtKeypoints[line.to];
      if (!fromRaw || !toRaw) return null;
      
      // Convert from native video coordinates to display coordinates
      const { x: baseDisplayFromX, y: baseDisplayFromY } = convertVideoCoordinates(
        fromRaw.x, fromRaw.y, nativeWidth, nativeHeight, displayDimensions.width, displayDimensions.height
      );
      const { x: baseDisplayToX, y: baseDisplayToY } = convertVideoCoordinates(
        toRaw.x, toRaw.y, nativeWidth, nativeHeight, displayDimensions.width, displayDimensions.height
      );
      
      // Apply zoom and pan transforms
      const from = applyZoomPan({ x: baseDisplayFromX, y: baseDisplayFromY });
      const to = applyZoomPan({ x: baseDisplayToX, y: baseDisplayToY });
      const length = Math.sqrt(Math.pow(to.x - from.x, 2) + Math.pow(to.y - from.y, 2));
      const angle = Math.atan2(to.y - from.y, to.x - from.x) * 180 / Math.PI;
      return (
        <CourtLine
          key={`${isReprojected ? 'reprojected' : isPredicted ? 'predicted' : 'primary'}-${index}`}
          color={getLineColor(line.type, isReprojected, isPredicted)}
          style={{
            left: from.x,
            top: from.y,
            width: length,
            height: isReprojected || isPredicted ? 2 : 3, // Thinner lines for reprojected/predicted courts
            transform: `rotate(${angle}deg)` ,
            transformOrigin: '0 0',
            zIndex: zIndex,
            opacity: isReprojected || isPredicted ? 0.6 : 1.0 // More transparent for reprojected/predicted courts
          }}
        />
      );
    });
  };

  // Render court lines (legacy function for backward compatibility)
  const renderCourtLines = () => {
    return renderCourtLinesForCourt(currentKeypoints, false, false, 2);
  };

  // Render keypoints for a specific court
  const renderKeypointsForCourt = (courtKeypoints, isReprojected = false, isPredicted = false, courtIndex = 0) => {
    if (!courtKeypoints) return null;
    
    // Get video dimensions for coordinate conversion
    const nativeWidth = (videoRef?.current?.width) || VIDEO_WIDTH;
    const nativeHeight = (videoRef?.current?.height) || VIDEO_HEIGHT;
    
    return COURT_KEYPOINT_LABELS.map(label => {
      const keypoint = courtKeypoints[label];
      if (!keypoint) return null;
      
      // Convert from native video coordinates to display coordinates
      const { x: baseDisplayX, y: baseDisplayY } = convertVideoCoordinates(
        keypoint.x, keypoint.y, nativeWidth, nativeHeight, displayDimensions.width, displayDimensions.height
      );
      
      // Apply zoom and pan transforms
      const { x, y } = applyZoomPan({ x: baseDisplayX, y: baseDisplayY });
      
      // Only allow interaction with primary court keypoints
      const isInteractive = !isReprojected && !isPredicted;
      
      return (
        <Keypoint
          key={`${isReprojected ? 'reprojected' : isPredicted ? 'predicted' : 'primary'}-${courtIndex}-${label}`}
          color={getKeypointColor(label, isReprojected, isPredicted)}
          outOfFrame={keypoint.outOfFrame}
          style={{
            left: x,
            top: y,
            opacity: isReprojected || isPredicted ? 0.6 : 1.0,
            zIndex: isReprojected || isPredicted ? 1 : 6,
            cursor: isInteractive ? 'grab' : 'default',
            pointerEvents: isInteractive ? 'auto' : 'none'
          }}
          onMouseDown={isInteractive ? (e) => handleMouseDown(e, label) : undefined}
          onContextMenu={isInteractive ? (e) => handleContextMenu(e, label) : undefined}
          title={`${label} - ${COURT_KEYPOINT_TYPES[label]}${keypoint.outOfFrame ? ' (Out of Frame)' : ''}${isReprojected ? ' (Reprojected)' : isPredicted ? ' (Predicted)' : ' - Right-click to toggle out-of-frame'}`}
        >
          {label}
        </Keypoint>
      );
    });
  };

  return (
    <CourtOverlayContainer 
      ref={overlayRef}
      displayWidth={displayDimensions.width}
      displayHeight={displayDimensions.height}
    >
      <CourtBackground />
      
      {/* Render all courts - reprojected/predicted courts first (in background) */}
      {allCourts.map((court, index) => (
        <React.Fragment key={`court-${index}`}>
          {/* Render court lines */}
          {court.keypoints && renderCourtLinesForCourt(court.keypoints, court.reprojected, court.predicted, court.reprojected || court.predicted ? 1 : 2)}
          
          {/* Render keypoints */}
          {court.keypoints && renderKeypointsForCourt(court.keypoints, court.reprojected, court.predicted, index)}
        </React.Fragment>
      ))}
    </CourtOverlayContainer>
  );
};

export default CourtOverlay; 